import { IdsAdapterService, LanguagesService, ListDrawerComponent } from '@alpha-core/platform-ui';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { GroupService } from '../../../services/group.service';
import { ADMIN_GROUP } from '../../../shared/alpha-constants';
import { AlphaSharedModule, PrimeNGModules } from '../../../shared/alpha-shared.module';
import { DialogComponent } from '../../dialog/dialog.component';
import { PaginationBarComponent } from '../../pagination-bar/pagination-bar.component';
import { Group } from '../group.type';

@Component({
    selector: 'app-group-table',
    standalone: true,
    imports: [AlphaSharedModule, PaginationBarComponent, ...PrimeNGModules, ListDrawerComponent],
    providers: [DialogService],
    templateUrl: './group-table.component.html',
    styleUrl: './group-table.component.scss',
})
export class GroupTableComponent implements OnInit, OnDestroy {
    private groupService = inject(GroupService);
    private router = inject(Router);
    private messageService = inject(MessageService);
    private dialogService = inject(DialogService);
    private route = inject(ActivatedRoute);
    private languageService = inject(LanguagesService);
    private idsAdapterService = inject(IdsAdapterService);

    private searchSubject$ = new Subject<void>();
    private destroy$ = new Subject<void>();

    admin_group = ADMIN_GROUP;
    groups: any[] = [];
    currentGroups: any[] = [];
    sort: { [key: string]: boolean } = {
        group_name: false,
        description: false,
        number_of_users: false,
        updatedAt: false,
    };
    sortStatus: 'asc' | 'desc' = 'asc';
    dropdownIconClass = 'pi pi-chevron-down';

    userVisible: boolean = false;
    filteredUsers: { userName: string }[] = [];
    users: { userName: string }[] = [];

    filter: {
        pageSize: number;
        page: number;
        totalCount: number;
        searchKey: string;
    } = { pageSize: 10, page: 0, totalCount: 0, searchKey: '' };

    menuOptions = [
        {
            label: 'default-cm-edit',
            icon: 'assets/icons/edit-button.svg',
            command: (a: any) => {
                this.navigateToUpdate(this.group);
            },
        },
        {
            label: 'default-cm-delete',
            icon: 'assets/icons/delete-button.svg',
            command: (a: any) => {
                this.deleteGroup(this.group);
            },
        },
    ];

    ngOnInit(): void {
        this.filter.pageSize = this.groupService.groupPageSize || this.filter.pageSize;
        this.searchSubject$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
            this.filter.page = 0;
            this.fetchGroups();
        });
        this.fetchGroups();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    fetchGroups(options?: { sortBy?: string; sortOrder?: 'asc' | 'desc' }) {
        this.idsAdapterService
            .fetchAllGroups({
                pageSize: this.filter.pageSize,
                pageNumber: this.filter.page,
                search: this.filter.searchKey,
                sortBy: options?.sortBy,
                sortOrder: options?.sortOrder,
            })
            .then((data) => {
                this.groups = data.groups;
                this.currentGroups = data.groups;
                this.filter.totalCount = data.totalCount;
            });
    }

    customSort(field: string) {
        if (this.sort[field]) {
            this.sortStatus = this.sortStatus === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortStatus = 'asc';
            this.sort[field] = true;
            Object.keys(this.sort).forEach((sortKey) => {
                if (sortKey !== field) {
                    this.sort[sortKey] = false;
                }
            });
        }
        this.fetchGroups({ sortBy: field, sortOrder: this.sortStatus });
    }

    onDropdownOpen() {
        this.dropdownIconClass = 'pi pi-chevron-up';
    }

    onDropdownClose() {
        this.dropdownIconClass = 'pi pi-chevron-down';
    }

    changePageSize(event: any) {
        if (event.value) {
            this.filter.pageSize = this.groupService.groupPageSize = event.value;
            this.filter.page = 0;
            this.fetchGroups();
        }
    }

    paginationChange(pagination: any) {
        this.filter.page = pagination.page;
        this.filter.pageSize = pagination.pageSize;
        this.fetchGroups();
    }

    navigateToCreateGroup() {
        this.groupService.navigateTo(['home', 'groups', 'create']);
    }

    group!: Group;
    openToggleMenu(menu: any, group: Group, event: any) {
        this.group = group;
        event.preventDefault();
        event.stopPropagation();
        menu.toggle(event);
    }

    deleteGroup(group: Group) {
        this.dialogService
            .open(DialogComponent, {
                data: {
                    dialogType: 'confirm',
                },
                header: this.languageService.getTranslation('default-cm-confirm') || 'Confirm',
                width: '35rem',
            })
            .onClose.subscribe((data: any) => {
                if (data) {
                    this.groupService
                        .deleteGroup(group)
                        .then(() => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Deleted!',
                                detail: `Group ${group.group_name} has been deleted.`,
                            });
                            this.fetchGroups();
                        })
                        .catch((error) => {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Failed',
                                detail: error?.error?.message || error?.error?.stack?.message || 'Internal error',
                            });
                        });
                }
            });
    }

    navigateToUpdate(group: Group) {
        const queryParams = this.route.snapshot.queryParams;
        this.router.navigate(['home/groups/update'], {
            queryParams,
            state: { data: group },
        });
    }

    searchGroup() {
        this.filter.searchKey = this.filter.searchKey.toLowerCase();
        this.searchSubject$.next();
    }

    handleBulkClick(groupName: string) {
        this.groupService.getUsersByGroup(groupName, false).subscribe((data) => {
            this.userVisible = true;
            this.users = data;
            this.filteredUsers = data;
        });
    }

    filterUsers(searchQuery: string) {
        this.filteredUsers = this.users.filter((user) => user.userName.toLowerCase().includes(searchQuery));
    }

    visibleChange(visible: boolean) {
        this.userVisible = visible;
    }
}
